import { AchievementData, AchievementId } from '../types/Achievement';
import { GameProgress } from '../types/GameProgress';
import { AILevel } from '../ai/types';
import { MONSTER_DATABASE, HIDDEN_MONSTER_IDS } from '../constants/monsters';
import { CHALLENGE_STAGES } from '../constants/challengeConfig';

export const ACHIEVEMENT_STORAGE_KEY = 'monster-buttle-achievements';

const AI_LEVEL_ORDER: AILevel[] = [
  AILevel.LV1,
  AILevel.LV2,
  AILevel.LV3,
  AILevel.LV4,
  AILevel.LV5,
  AILevel.LV6,
];

const TOURNAMENT_ELIMINATION_IDS: AchievementId[] = [
  AchievementId.TOURNAMENT_ELIMINATION_CHAMPION_LV1,
  AchievementId.TOURNAMENT_ELIMINATION_CHAMPION_LV2,
  AchievementId.TOURNAMENT_ELIMINATION_CHAMPION_LV3,
  AchievementId.TOURNAMENT_ELIMINATION_CHAMPION_LV4,
  AchievementId.TOURNAMENT_ELIMINATION_CHAMPION_LV5,
  AchievementId.TOURNAMENT_ELIMINATION_CHAMPION_LV6,
];

const TOURNAMENT_ROUND_ROBIN_IDS: AchievementId[] = [
  AchievementId.TOURNAMENT_ROUND_ROBIN_CHAMPION_LV1,
  AchievementId.TOURNAMENT_ROUND_ROBIN_CHAMPION_LV2,
  AchievementId.TOURNAMENT_ROUND_ROBIN_CHAMPION_LV3,
  AchievementId.TOURNAMENT_ROUND_ROBIN_CHAMPION_LV4,
  AchievementId.TOURNAMENT_ROUND_ROBIN_CHAMPION_LV5,
  AchievementId.TOURNAMENT_ROUND_ROBIN_CHAMPION_LV6,
];

const SURVIVAL_MILESTONE_IDS: AchievementId[] = [
  AchievementId.SURVIVAL_WIN_10,
  AchievementId.SURVIVAL_WIN_20,
  AchievementId.SURVIVAL_WIN_30,
  AchievementId.SURVIVAL_WIN_40,
  AchievementId.SURVIVAL_WIN_50,
  AchievementId.SURVIVAL_WIN_60,
  AchievementId.SURVIVAL_WIN_70,
  AchievementId.SURVIVAL_WIN_80,
  AchievementId.SURVIVAL_WIN_90,
  AchievementId.SURVIVAL_WIN_100,
];

const PLAYABLE_MONSTER_IDS: string[] = MONSTER_DATABASE
  .map((m) => m.id)
  .filter((id) => !HIDDEN_MONSTER_IDS.includes(id));

const VALID_ACHIEVEMENT_IDS = new Set<string>(Object.values(AchievementId));

function parseAchievementData(obj: unknown): AchievementData | null {
  if (!obj || typeof obj !== 'object') return null;
  const candidate = obj as Record<string, unknown>;
  if (!Array.isArray(candidate.unlocked)) return null;
  const unlocked = candidate.unlocked.filter(
    (id): id is AchievementId => VALID_ACHIEVEMENT_IDS.has(id as string),
  );
  return { unlocked };
}

export function loadAchievements(): AchievementData {
  try {
    const raw = localStorage.getItem(ACHIEVEMENT_STORAGE_KEY);
    if (!raw) return { unlocked: [] };
    const parsed: unknown = JSON.parse(raw);
    return parseAchievementData(parsed) ?? { unlocked: [] };
  } catch {
    return { unlocked: [] };
  }
}

function saveAchievements(data: AchievementData): void {
  try {
    localStorage.setItem(ACHIEVEMENT_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // LocalStorageが使えない場合は無視
  }
}

export function isUnlocked(id: AchievementId): boolean {
  return loadAchievements().unlocked.includes(id);
}

export function unlockAchievements(ids: AchievementId[]): AchievementId[] {
  if (ids.length === 0) return [];
  const data = loadAchievements();
  const alreadyUnlocked = new Set(data.unlocked);
  const toUnlock = [...new Set(ids)].filter((id) => !alreadyUnlocked.has(id));
  if (toUnlock.length === 0) return [];
  saveAchievements({ unlocked: [...data.unlocked, ...toUnlock] });
  return toUnlock;
}

export function checkCampaignAchievements(progress: GameProgress): AchievementId[] {
  const result: AchievementId[] = [];

  if (progress.clearedStages >= CHALLENGE_STAGES.length) {
    result.push(AchievementId.CAMPAIGN_ALL_STAGES);
  }

  const defeatedSet = new Set(progress.bossDefeatedWith);
  const allDefeated = PLAYABLE_MONSTER_IDS.every((id) => defeatedSet.has(id));
  if (allDefeated) {
    result.push(AchievementId.CAMPAIGN_ALL_MONSTERS);
  }

  return result;
}

export function checkTournamentEliminationAchievements(aiLevel: AILevel): AchievementId[] {
  const levelIndex = AI_LEVEL_ORDER.indexOf(aiLevel);
  if (levelIndex < 0) return [];
  return TOURNAMENT_ELIMINATION_IDS.slice(0, levelIndex + 1);
}

export function checkTournamentRoundRobinAchievements(aiLevel: AILevel): AchievementId[] {
  const levelIndex = AI_LEVEL_ORDER.indexOf(aiLevel);
  if (levelIndex < 0) return [];
  return TOURNAMENT_ROUND_ROBIN_IDS.slice(0, levelIndex + 1);
}

export function checkSurvivalAchievements(winCount: number): AchievementId[] {
  return SURVIVAL_MILESTONE_IDS.filter((_, i) => winCount >= (i + 1) * 10);
}

export function checkHiddenCharacterAchievements(unlockedIds: string[]): AchievementId[] {
  const result: AchievementId[] = [];
  if (unlockedIds.includes('alice')) result.push(AchievementId.UNLOCK_ALICE);
  if (unlockedIds.includes('mococo')) result.push(AchievementId.UNLOCK_MOCOCO);
  return result;
}
